import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import PreApprovalForm from './PreApprovalForm';

const { FiCalculator, FiMessageCircle, FiCreditCard, FiFileText } = FiIcons;

function AIFinanceAdvisor() {
  const [question, setQuestion] = useState('');
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleCalculate = () => {
    setIsCalculating(true);
    setTimeout(() => {
      setPaymentPlan({
        monthlyPayment: 787, // CAD pricing
        totalAmount: 47220,
        interestRate: 5.9, // Canadian rates
        term: 60,
        downPayment: 5000,
        hst: 6750 // 15% HST
      });
      setIsCalculating(false);
    }, 2000);
  };

  const commonQuestions = [
    "What's my payment if I put $5,000 down for 60 months?",
    "Should I lease or buy this vehicle in Halifax?",
    "What interest rate can I qualify for in Canada?",
    "How does HST affect my financing?"
  ];

  return (
    <section id="finance" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Halifax AI Finance Advisor
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get personalized Canadian financing advice and instant payment calculations from our Halifax AI financial expert.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Chat Interface */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center space-x-3 mb-6">
                <SafeIcon icon={FiMessageCircle} className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Ask Your Halifax Finance Questions</h3>
              </div>
              <div className="space-y-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                  <p className="text-gray-700">
                    💡 <strong>Halifax AI Finance Advisor:</strong> I can help you with Canadian payment calculations, 
                    financing options, lease vs buy decisions for Maritime drivers, and HST considerations. What would you like to know?
                  </p>
                </div>
              </div>
              <div className="relative mb-6">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask me anything about Halifax financing... e.g., 'What's my payment if I put $5,000 down for 60 months on a $45,000 car with HST?'"
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none resize-none h-24"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCalculate}
                  className="absolute bottom-4 right-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <SafeIcon icon={FiCalculator} className="w-4 h-4" />
                  <span>Calculate</span>
                </motion.button>
              </div>

              {isCalculating && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Halifax AI is calculating...</span>
                </div>
              )}

              {paymentPlan && !isCalculating && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200"
                >
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Your Halifax Payment Plan</h4>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        ${paymentPlan.monthlyPayment} CAD
                      </div>
                      <p className="text-sm text-gray-600">Monthly Payment</p>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-semibold text-gray-900">
                        {paymentPlan.interestRate}% APR
                      </div>
                      <p className="text-sm text-gray-600">Canadian Interest Rate</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Down Payment:</span>
                      <span>${paymentPlan.downPayment.toLocaleString()} CAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span>HST (15%):</span>
                      <span>${paymentPlan.hst.toLocaleString()} CAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Term:</span>
                      <span>{paymentPlan.term} months</span>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>Total Amount:</span>
                      <span>${paymentPlan.totalAmount.toLocaleString()} CAD</span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiFileText} className="w-5 h-5" />
                    <span>Apply for Halifax Financing</span>
                  </motion.button>
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Quick Options */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Halifax Common Questions</h4>
              <div className="space-y-2">
                {commonQuestions.map((q, index) => (
                  <button
                    key={index}
                    onClick={() => setQuestion(q)}
                    className="w-full text-left p-3 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiCreditCard} className="w-6 h-6 text-green-600" />
                <h4 className="font-semibold text-gray-900">Halifax Pre-Approval</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Get pre-approved in minutes and shop with confidence knowing your Halifax budget.
              </p>
              <PreApprovalForm />
            </div>

            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-2">Halifax Financing Benefits</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Competitive Canadian rates starting at 3.9%</li>
                <li>• Flexible terms up to 84 months</li>
                <li>• No prepayment penalties</li>
                <li>• Same-day Halifax approval available</li>
                <li>• HST financing options</li>
              </ul>
            </div>
          </motion.div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            * All Halifax financing subject to credit approval. Terms and conditions apply. Contact our Halifax finance team for complete details.
          </p>
        </div>
      </div>
    </section>
  );
}

export default AIFinanceAdvisor;